import React, { useState } from 'react';
import { Box, AppBar, Toolbar, Container, Button, Stack, useTheme, useMediaQuery, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Menu as MenuIcon, Close as CloseIcon, Brightness4, Brightness7 } from '@mui/icons-material';
import NewHero from './NewHero';
import NewFeaturesSection from './NewFeaturesSection';
import HowItWorksSection from './HowItWorksSection';
import StatsSection from './StatsSection';
import TestimonialsSection from './TestimonialsSection';
import NewCTASection from './NewCTASection';
import NewFooter from './NewFooter';
import AuthenticationPageNew from './AuthenticationPageNew';
import SearchDashboard from '../SearchDashboard/SearchDashboard';

const LandingPage = ({ onNavigateToApp, currentUser, toggleDarkMode, landingSearchProps = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isDark = theme.palette.mode === 'dark';

  const surfaceColor = isDark ? '#0f172a' : '#ffffff';
  const toolbarBg = alpha(theme.palette.background.paper, isDark ? 0.92 : 0.9);
  const toolbarBorder = alpha(theme.palette.divider, isDark ? 0.4 : 0.16);
  const brandColor = isDark ? theme.palette.primary.light : '#1d4ed8';
  const outlineHover = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12);
  const actionGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`;

  const navItems = ['Features', 'How It Works', 'Testimonials'];
  const navigateToBlog = () => {
    if (typeof window === 'undefined') return;
    window.location.href = '/blog';
  };

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleLoginSuccess = (user) => {
    onNavigateToApp(user);
  };

  if (showAuth) {
    return (
      <AuthenticationPageNew
        onLoginSuccess={handleLoginSuccess}
        onBack={() => setShowAuth(false)}
        initialMode={authMode}
      />
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: surfaceColor,
        color: theme.palette.text.primary,
        fontFamily: '"Manrope", "Inter", sans-serif',
        minHeight: '100vh',
      }}
    >
      {/* Navigation Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: toolbarBg,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${toolbarBorder}`,
          backdropFilter: 'blur(20px)',
          boxShadow: isDark 
            ? '0 4px 30px rgba(0, 0, 0, 0.3)' 
            : '0 2px 20px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Toolbar sx={{ py: 1.5, px: { xs: 2, md: 4 } }}>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                background: actionGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                fontSize: '1.1rem',
                color: '#fff',
                fontFamily: '"Sora", sans-serif',
                letterSpacing: -1,
                boxShadow: `0 4px 20px ${alpha(brandColor, 0.35)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  animation: 'shimmer 3s infinite',
                },
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' },
                },
              }}
            >
              CFP
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  background: actionGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily: '"Sora", "Inter", sans-serif',
                  letterSpacing: -0.5,
                  lineHeight: 1.2,
                }}
              >
                Carrier Finder Pro
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: isDark ? alpha('#94a3b8', 0.8) : alpha('#64748b', 0.9),
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  mt: -0.3,
                }}
              >
                FMCSA Verified
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              {navItems.map((item) => (
                <Button
                  key={item}
                  color="inherit"
                  sx={{
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1,
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s',
                    '&:hover': {
                      color: brandColor,
                      background: isDark ? alpha(brandColor, 0.1) : alpha(brandColor, 0.08),
                      transform: 'translateY(-1px)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: 0,
                      height: '2px',
                      background: actionGradient,
                      transition: 'all 0.3s',
                      transform: 'translateX(-50%)',
                    },
                    '&:hover::after': {
                      width: '70%',
                    },
                  }}
                  onClick={() => handleScroll(item.toLowerCase().replace(/ /g, ''))}
                >
                  {item}
                </Button>
              ))}
              
              {/* Dark Mode Toggle */}
              <Tooltip title={isDark ? 'Light Mode' : 'Dark Mode'} arrow>
                <IconButton 
                  onClick={toggleDarkMode} 
                  sx={{ 
                    ml: 1,
                    background: isDark ? alpha('#334155', 0.4) : alpha('#f1f5f9', 0.8),
                    '&:hover': {
                      background: isDark ? alpha('#334155', 0.6) : alpha('#e2e8f0', 1),
                      transform: 'rotate(180deg)',
                    },
                    transition: 'all 0.4s',
                  }}
                >
                  {isDark ? <Brightness7 /> : <Brightness4 />}
                </IconButton>
              </Tooltip>

              <Button
                color="inherit"
                sx={{
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  px: 2.5,
                  py: 1,
                  borderRadius: 2,
                }}
                onClick={navigateToBlog}
              >
                Blog
              </Button>
              <Button
                variant="outlined"
                sx={{
                  ml: 2,
                  borderColor: brandColor,
                  color: brandColor,
                  fontWeight: 700,
                  borderWidth: 2,
                  px: 3,
                  py: 1,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s',
                  '&:hover': { 
                    borderColor: brandColor, 
                    borderWidth: 2,
                    backgroundColor: outlineHover,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(brandColor, 0.2)}`,
                  },
                }}
                onClick={() => {
                  setAuthMode('login');
                  setShowAuth(true);
                }}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                sx={{
                  ml: 1.5,
                  background: actionGradient,
                  fontWeight: 700,
                  px: 3.5,
                  py: 1,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: `0 4px 16px ${alpha(brandColor, 0.3)}`,
                  transition: 'all 0.3s',
                  '&:hover': {
                    boxShadow: `0 6px 24px ${alpha(brandColor, 0.45)}`,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => {
                  setAuthMode('signup');
                  setShowAuth(true);
                }}
              >
                Get Started →
              </Button>
            </Stack>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={isDark ? 'Light Mode' : 'Dark Mode'} arrow>
                <IconButton 
                  onClick={toggleDarkMode}
                  sx={{
                    background: isDark ? alpha('#334155', 0.4) : alpha('#f1f5f9', 0.8),
                    '&:hover': {
                      background: isDark ? alpha('#334155', 0.6) : alpha('#e2e8f0', 1),
                    },
                  }}
                >
                  {isDark ? <Brightness7 /> : <Brightness4 />}
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  background: isDark ? alpha('#334155', 0.4) : alpha('#f1f5f9', 0.8),
                  '&:hover': {
                    background: isDark ? alpha('#334155', 0.6) : alpha('#e2e8f0', 1),
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            background: isDark 
              ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: actionGradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 900,
                  fontSize: '0.9rem',
                  color: '#fff',
                  fontFamily: '"Sora", sans-serif',
                  letterSpacing: -1,
                }}
              >
                CFP
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: isDark ? '#f1f5f9' : '#0f172a', fontFamily: '"Sora", sans-serif' }}>
                Menu
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                background: isDark ? alpha('#334155', 0.4) : alpha('#f1f5f9', 0.8),
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <List sx={{ mb: 2 }}>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigateToBlog();
                  setMobileMenuOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  '&:hover': {
                    background: isDark ? alpha(brandColor, 0.15) : alpha(brandColor, 0.1),
                  },
                }}
              >
                <ListItemText
                  primary="Blog"
                  primaryTypographyProps={{
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                />
              </ListItemButton>
            </ListItem>
            {navItems.map((item) => (
              <ListItem key={item} disablePadding sx={{ mb: 1 }}>
                <ListItemButton 
                  onClick={() => handleScroll(item.toLowerCase().replace(/ /g, ''))}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    '&:hover': {
                      background: isDark ? alpha(brandColor, 0.15) : alpha(brandColor, 0.1),
                    },
                  }}
                >
                  <ListItemText 
                    primary={item}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Stack spacing={1.5} sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              sx={{
                borderColor: brandColor,
                color: brandColor,
                fontWeight: 700,
                borderWidth: 2,
                py: 1.5,
                borderRadius: 2.5,
                textTransform: 'none',
                '&:hover': {
                  borderWidth: 2,
                  borderColor: brandColor,
                  background: outlineHover,
                },
              }}
              onClick={() => {
                setAuthMode('login');
                setShowAuth(true);
                setMobileMenuOpen(false);
              }}
            >
              Sign In
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{ 
                background: actionGradient,
                fontWeight: 700,
                py: 1.5,
                borderRadius: 2.5,
                textTransform: 'none',
                boxShadow: `0 4px 16px ${alpha(brandColor, 0.3)}`,
              }}
              onClick={() => {
                setAuthMode('signup');
                setShowAuth(true);
                setMobileMenuOpen(false);
              }}
            >
              Get Started →
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box>
        {/* Hero Section */}
        <NewHero onGetStarted={() => {
          setAuthMode('signup');
          setShowAuth(true);
        }} />

        {landingSearchProps ? (
          <Box
            sx={{
              px: { xs: 2, md: 4 },
              py: { xs: 4, md: 6 },
              background: isDark
                ? 'linear-gradient(180deg, rgba(15,23,42,0.92) 0%, rgba(30,41,59,0.98) 100%)'
                : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.28 : 0.12)}`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.28 : 0.12)}`,
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  width: '100%',
                  mb: 3.5,
                  p: { xs: 2.25, md: 3.25 },
                  borderRadius: 4,
                  border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}`,
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(37,99,235,0.10) 0%, rgba(15,23,42,0.22) 52%, rgba(16,185,129,0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(37,99,235,0.06) 0%, rgba(255,255,255,0.88) 50%, rgba(16,185,129,0.06) 100%)',
                  boxShadow: isDark
                    ? '0 18px 40px rgba(2, 8, 23, 0.24)'
                    : '0 18px 40px rgba(15, 23, 42, 0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -50,
                    right: -40,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
                    filter: 'blur(12px)',
                  }}
                />
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    mb: 1.5,
                    borderRadius: 999,
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: isDark ? '#93c5fd' : '#1d4ed8',
                    backgroundColor: isDark ? alpha('#2563eb', 0.16) : alpha('#2563eb', 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.28 : 0.14)}`,
                  }}
                >
                  Live FMCSA Search
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: '2.1rem', md: '3.4rem' },
                    fontWeight: 900,
                    letterSpacing: -1.8,
                    mb: 1.1,
                    maxWidth: 880,
                    lineHeight: 0.98,
                    background: isDark
                      ? 'linear-gradient(135deg, #f8fafc 0%, #93c5fd 42%, #86efac 100%)'
                      : 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 48%, #059669 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: '"Sora", "Inter", sans-serif',
                  }}
                >
                  Find verified carriers in seconds
                </Typography>
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    lineHeight: 1.8,
                    maxWidth: 920,
                    fontSize: { xs: '0.98rem', md: '1.05rem' },
                  }}
                >
                  Search carriers by MC number, DOT number, or company name, then switch to batch lookup when you need to review multiple records in one streamlined workflow.
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.2}
                  useFlexGap
                  sx={{ mt: 2.2, position: 'relative', zIndex: 1 }}
                >
                  {['Single and batch search', 'Instant FMCSA lookup', 'Built for professional teams'].map((item) => (
                    <Box
                      key={item}
                      sx={{
                        px: 1.4,
                        py: 0.9,
                        borderRadius: 2.5,
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        backgroundColor: isDark ? alpha('#ffffff', 0.05) : alpha('#ffffff', 0.86),
                        border: `1px solid ${alpha(theme.palette.divider, isDark ? 0.24 : 0.8)}`,
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      {item}
                    </Box>
                  ))}
                </Stack>
              </Box>

              <SearchDashboard {...landingSearchProps} />
            </Container>
          </Box>
        ) : null}

        {/* Features Section */}
        <Box id="features">
          <NewFeaturesSection />
        </Box>

        {/* How It Works Section */}
        <Box id="howitworks">
          <HowItWorksSection />
        </Box>

        {/* Stats Section */}
        <Box id="stats">
          <StatsSection />
        </Box>

        {/* Testimonials Section */}
        <Box id="testimonials">
          <TestimonialsSection />
        </Box>

        {/* CTA Section */}
        <NewCTASection onGetStarted={() => {
          setAuthMode('signup');
          setShowAuth(true);
        }} />

        {/* Footer */}
        <NewFooter />
      </Box>
    </Box>
  );
};

export default LandingPage;
